import { useQuery } from 'react-query';
import { AxiosError } from 'axios';
import { Axios } from '@utils/api/axios-instance.ts';
import { GET_URL } from '@utils/api/endpoints.ts';
import { BaseError } from '@utils/types/shared/base.ts';
import { Subscription, SubscriptionResponse } from './use-get-subscription.tsx';

/**
 * Custom-model yearly allowance + per-round cost for the caller's active
 * subscription. Powers the "3 of 4 rounds · €99/round" card on the custom
 * model page (#2211, criterion 5).
 *
 * There is no single "get my active subscription" endpoint. The panel
 * already loads the whole list on the Billing page via
 * ``useGetAllSubscriptions``; here we make a small, standalone query so the
 * custom-model page doesn't depend on the billing page's search-param
 * driven pagination. One list call, one detail call, both cached.
 *
 * "Active" = not cancelled and not suspended, whose window covers today.
 * ``end_date`` NULL means open-ended (unlimited-duration plan), still
 * active. When more than one active subscription is present the first is
 * used; a customer with several concurrent plans is rare and the yearly
 * training cap already resolves to the most generous limit across all of
 * them (``effective_custom_model_yearly_limit``), so the exact pick barely
 * matters — the card is informational.
 */

interface SubscriptionListItem {
  id: number;
  sku: string;
  start_date: string | null;
  end_date: string | null;
  cancel_date: string | null;
  auto_renew: boolean;
  suspended: boolean;
  billing_channel: string;
}

interface SubscriptionListResponse {
  count: number;
  total_results: number;
  result: SubscriptionListItem[];
}

const pickActive = (
  items: SubscriptionListItem[]
): SubscriptionListItem | null => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const inWindow = (s: SubscriptionListItem) => {
    if (s.cancel_date) return false;
    if (s.suspended) return false;
    // Some plans return end_date = "" rather than null; treat both as open.
    if (s.end_date && s.end_date.trim() !== '') {
      const end = new Date(s.end_date);
      if (!Number.isNaN(end.getTime()) && end < today) return false;
    }
    if (s.start_date && s.start_date.trim() !== '') {
      const start = new Date(s.start_date);
      if (!Number.isNaN(start.getTime()) && start > today) return false;
    }
    return true;
  };
  const active = items.filter(inWindow);
  if (active.length === 0) return null;
  // Prefer the most recently started active subscription.
  return active.sort((a, b) => {
    const ta = a.start_date ? Date.parse(a.start_date) : 0;
    const tb = b.start_date ? Date.parse(b.start_date) : 0;
    return tb - ta;
  })[0];
};

export interface CustomModelAllowance {
  used: number;
  limit: number | null; // null = unlimited
  remaining: number | null;
  unitCost: number | null;
  subscriptionId: number | null;
}

const toNumber = (v: string | number | null | undefined): number | null => {
  if (v == null) return null;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : null;
};

async function fetchAllowance(): Promise<CustomModelAllowance> {
  // Pull enough of the list to cover the pathological case of a customer
  // with dozens of legacy subscriptions; the default page size is small.
  const listUrl = `/api/v3/subscription?offset=0&limit=100`;
  const list = await Axios.get<SubscriptionListResponse>(listUrl);
  const active = pickActive(list.data.result ?? []);
  if (!active) {
    return {
      used: 0,
      limit: null,
      remaining: null,
      unitCost: null,
      subscriptionId: null
    };
  }
  const detail = await Axios.get<SubscriptionResponse>(
    `${GET_URL(String(active.id)).subscription}`
  );
  const sub: Subscription | undefined = detail.data?.result;
  return {
    used: sub?.custom_model_yearly_used ?? 0,
    limit: sub?.custom_model_yearly_limit ?? null,
    remaining: sub?.custom_model_yearly_remaining ?? null,
    unitCost: toNumber(sub?.automatic_training_unit_cost),
    subscriptionId: active.id
  };
}

export const useCurrentCustomModelAllowance = () =>
  useQuery<CustomModelAllowance, AxiosError<BaseError>>(
    ['current-custom-model-allowance'],
    fetchAllowance,
    {
      // Yearly counters move on training round success, which is a click,
      // never a stream. Keep the query cheap.
      staleTime: 60_000,
      refetchInterval: 60_000,
      useErrorBoundary: false,
      // Never toast — this card is informational.
      onError: () => {}
    }
  );
